import React, { createContext, useContext, useState, useEffect } from 'react';

export interface User {
  id: number;
  name: string;
  username: string;
  role: 'admin' | 'principal' | 'teacher';
  status: 'active' | 'inactive';
}

export interface School {
  id: number;
  school_name: string;
  school_code: string;
  district: string;
  state: string;
  address?: string;
  principal_name?: string;
  principal_mobile?: string;
  official_email?: string;
  active_session_id?: number;
  active_session_name?: string;
}

export interface TeacherAssignment {
  id: number;
  section: string;
  board_id: number;
  class_id: number;
  subject_id: number;
  board_name: string;
  board_code: string;
  class_name: string;
  subject_name: string;
  subject_code?: string;
}

export interface TeacherProfile {
  id: number;
  user_id: number;
  teacher_name: string;
  mobile: string;
  email?: string;
  designation: string;
  status: string;
  assignments: TeacherAssignment[];
}

interface AuthContextType {
  user: User | null;
  school: School | null;
  teacherProfile: TeacherProfile | null;
  token: string | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  refreshMe: () => Promise<void>;
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
}

let inMemoryToken: string | null = null;

export function getAuthToken(): string | null {
  if (inMemoryToken && inMemoryToken !== 'null' && inMemoryToken !== 'undefined') {
    return inMemoryToken;
  }
  try {
    const t = localStorage.getItem('board_app_token') || 
              localStorage.getItem('token') ||
              sessionStorage.getItem('board_app_token') ||
              sessionStorage.getItem('token');
    if (t && t !== 'null' && t !== 'undefined') {
      inMemoryToken = t;
      return t;
    }
  } catch (e) {}
  return null;
}

export function setStoredAuthToken(token: string | null): void {
  inMemoryToken = token;
  try {
    if (token) {
      localStorage.setItem('board_app_token', token);
      localStorage.setItem('token', token);
      sessionStorage.setItem('board_app_token', token);
      sessionStorage.setItem('token', token);
    } else {
      localStorage.removeItem('board_app_token');
      localStorage.removeItem('token');
      sessionStorage.removeItem('board_app_token');
      sessionStorage.removeItem('token');
    }
  } catch (e) {}
}

export function getAuthHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = { ...extra };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [school, setSchool] = useState<School | null>(null);
  const [teacherProfile, setTeacherProfile] = useState<TeacherProfile | null>(null);
  const [token, setToken] = useState<string | null>(() => getAuthToken());
  const [loading, setLoading] = useState<boolean>(true);

  const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const currentToken = token || getAuthToken();
    const headers = new Headers(options.headers || {});
    if (currentToken) {
      headers.set('Authorization', `Bearer ${currentToken}`);
    }
    if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401) {
      if (typeof url === 'string' && url.includes('/api/auth/me')) {
        logout();
      }
    }
    return res;
  };

  const refreshMe = async () => {
    const currentToken = token || getAuthToken();
    if (!currentToken) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setUser(data.user);
          setSchool(data.school);
          setTeacherProfile(data.teacherProfile || null);
        } else {
          logout();
        }
      } else if (res.status === 401) {
        logout();
      }
    } catch (err) {
      console.error('Auth verification failed', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshMe();
  }, [token]);

  const login = async (username: string, password: string) => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (data.success && data.token) {
        setStoredAuthToken(data.token);
        setToken(data.token);
        setUser(data.user);
        setTeacherProfile(data.teacherProfile || null);
        // Refresh school info
        const meRes = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${data.token}` }
        });
        if (meRes.ok) {
          const meData = await meRes.json();
          setSchool(meData.school);
        }
        return { success: true };
      } else {
        return { success: false, message: data.message || 'Invalid credentials' };
      }
    } catch (err: any) {
      return { success: false, message: 'Network or server connection failed.' };
    }
  };

  const logout = () => {
    setStoredAuthToken(null);
    setToken(null);
    setUser(null);
    setSchool(null);
    setTeacherProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, school, teacherProfile, token, loading, login, logout, refreshMe, fetchWithAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
